import { useHitTarget } from '../responsive/hitTarget'
import './searchField.css'

function SearchIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <circle cx="11" cy="11" r="7" stroke="currentColor" strokeWidth="2.2" />
      <path d="M20 20l-4.3-4.3" stroke="currentColor" strokeWidth="2.2" />
    </svg>
  )
}

/**
 * The filter-a-list search pill — icon, input, no submit.
 *
 * ## Why this exists
 *
 * Merchants and categories had each written one, and they had already drifted:
 * different widths, and — until 2026-08-21 — different backdrops, which is how
 * the categories placeholder shipped at 4.25:1 (issue #300) while the merchants
 * one cleared its floor. Two controls doing the same job should not be able to
 * disagree about their own contrast.
 *
 * ## `type="search"` and a real label
 *
 * `type="search"` gives the platform clear-button and history affordances.
 * `label` is the accessible name and is REQUIRED — a placeholder is not a
 * label: it disappears on first keystroke, and a screen reader announcing
 * "Filter categories" only while the field is empty is announcing nothing when
 * it matters. It is applied as `aria-label` rather than a visible `<label>`
 * because the icon and the pill are the visual affordance.
 *
 * ## The hit target is on the INPUT
 *
 * `useHitTarget` measures the control, and the sweep found an earlier version
 * at 238x15 against a 44px coarse floor: the pill's height was never the
 * input's. The minimum goes on the element the sweep actually measures.
 */
export function SearchField({
  label,
  value,
  onChange,
  placeholder,
  className,
  maxLength,
  autoFocus,
}: {
  /** Accessible name. Required — see the docblock. */
  label: string
  value: string
  onChange: (value: string) => void
  placeholder?: string
  /** For the caller's own width/flex only. The SKIN is not overridable — an
   *  inline style cannot be given a hover state later, and a second backdrop
   *  is exactly the drift this primitive replaces. */
  className?: string
  /** The server's own limit on the query, where it has one — a longer query
   *  is a 422 rather than a search. */
  maxLength?: number
  /** Take focus on mount. For a search that IS the point of a dialog, whose
   *  content may arrive after the overlay's own focus-first-control has run. */
  autoFocus?: boolean
}) {
  const target = useHitTarget('page')

  return (
    <span className={['search-field', className].filter(Boolean).join(' ')}>
      <SearchIcon />
      <input
        type="search"
        className="search-field__input"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        maxLength={maxLength}
        autoFocus={autoFocus}
        aria-label={label}
        style={{ minHeight: target }}
      />
    </span>
  )
}
